import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useMsal } from '@azure/msal-react';
import { currentWorkflow } from '../../utils/Store';
import '../../styles/editor.css';
import '../../styles/components.css';
import logo from './../../images/Centella Logo 1.png';
import {
  User,
  LayoutDashboard,
  Pill,
  FlaskConical,
  CircleHelp,
  BrainCircuit,
  Filter,
  Bell,
  LogOut,
  PanelRightOpen,
  PanelRightClose,
  Dna,
  Recycle,
  Syringe,
  LockKeyhole,
  Atom,
  Cpu,
  FlaskRound
} from 'lucide-react';
import { isSidebarCollapsedAtom } from '../../atoms/sidebar';
import { useAtom } from 'jotai';

const SideNav = ({ handleCollapseOfParent }) => {
  const { instance, inProgress } = useMsal();
  const currentWflow = useAtomValue(currentWorkflow);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeLink, setActiveLink] = useState(location.pathname);
  const [isCollapsed, setIsCollapsed] = useAtom(isSidebarCollapsedAtom);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location]);

  const getLinkClasses = (path) => {
    const isActive =
      activeLink.includes(path) ||
      activeLink === path ||
      (activeLink === '/' && path === '/dashboard');

    if (isCollapsed) {
      return `flex items-center justify-center h-12 w-12 mx-auto rounded-xl transition-all duration-200 ${isActive ? 'bg-[#7364AD] text-white' : 'text-gray-600 hover:bg-gray-100'
        }`;
    }

    return `flex items-center h-12 px-3 mx-2 rounded-xl transition-all duration-200 ${isActive
      ? 'bg-[#7364AD] text-white shadow-sm'
      : 'text-gray-700 hover:bg-gray-50 hover:shadow-sm'
      }`;
  };

  const getIconColor = (path) => {
    const isActive =
      activeLink.includes(path) ||
      activeLink === path ||
      (activeLink === '/' && path === '/dashboard');
    return isActive ? 'text-current' : 'text-current';
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    instance.logoutRedirect();
  };

  const handleLogoutRedirect = () => {
    setShowLogoutModal(true);
  };

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    handleCollapseOfParent();
  };

  const NavItem = ({ to, icon: Icon, label, extraIcon: ExtraIcon }) => (
    <Link to={to} className={getLinkClasses(to)}>
      <div className="flex items-center justify-center min-w-[24px]">
        {ExtraIcon ? (
          <div className="flex items-center gap-0.5 ">
            <Icon size={16} className={getIconColor(to)} />
            <ExtraIcon size={12} className={getIconColor(to)} />
          </div>
        ) : (
          <Icon size={18} className={getIconColor(to)} />
        )}
      </div>
      {!isCollapsed && (
        <span className="ml-3 text-sm font-medium leading-none">{label}</span>
      )}
    </Link>
  );

  const SectionDivider = () =>
    !isCollapsed && <div className="mx-4 my-2 border-t border-gray-200" />;

  const SectionTitle = ({ title }) =>
    !isCollapsed && (
      <h3 className="px-4 py-2 text-xs font-bold text-[#735AA7] uppercase tracking-wider">
        {title}
      </h3>
    );

  return (
    <>
      {/* Sidebar */}
      <div
        className={`
        fixed left-0 top-0 z-40 h-screen bg-white shadow-lg transition-all duration-300 ease-in-out
        ${isCollapsed ? 'w-20' : 'w-64'}
        hidden md:flex md:flex-col
      `}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-100">
          <div
            className={`flex items-center ${isCollapsed ? 'justify-center w-full' : ''}`}
          >
            <img
              src={logo}
              className="h-8 w-7 flex-shrink-0"
              alt="Centella AI Logo"
            />
            {!isCollapsed && (
              <div className="ml-2">
                <div className="text-[#89AF3E] text-xs font-bold tracking-wide">
                  CASCADE
                </div>
                <div className="text-xs text-[#735DA8] font-medium">
                  CENTELLA AI
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Collapse toggle button - always visible */}
        <button
          onClick={handleCollapse}
          className={`absolute top-16 p-1 bg-white mt-2 shadow-md border text-gray-400 hover:text-gray-600 transition-colors z-10 ${isCollapsed ? '-right-2.5' : '-right-2.5'
            }`}
        >
          {isCollapsed ? (
            <PanelRightClose size={14} />
          ) : (
            <PanelRightOpen size={14} />
          )}
        </button>

        {/* Scrollable content - ALL elements scroll */}
        <div className="flex-1 overflow-y-scroll scrollbar scrollbar-thin scrollbar-thumb-[#A0AEC0] scrollbar-track-[#F4F4F4] min-h-0">
          <div className={`${isCollapsed ? 'py-4 px-2' : 'py-4 px-0'}`}>
            {/* Dashboard Section */}
            <div className={isCollapsed ? 'space-y-3 mb-6' : 'mb-4'}>
              {/* <SectionTitle title="Dashboard" /> */}
              <div className={isCollapsed ? 'space-y-3' : 'space-y-1'}>
                <NavItem
                  to="/dashboard"
                  icon={LayoutDashboard}
                  label="Dashboard"
                />
              </div>
              <SectionDivider />
              <div className={isCollapsed ? 'space-y-3' : 'space-y-1'}>
                <NavItem
                  to="/ChatCascade"
                  icon={Cpu}
                  // extraIcon={Syringe}
                  label="ChatCascade"
                />
              
              </div>
              <SectionDivider />
            </div>

            {/* Small Molecule Discovery Suite */}
            <div className={isCollapsed ? 'space-y-3 mb-6' : 'mb-4'}>
              <SectionTitle title="Small Molecule Discovery Suite" />
              <div className={isCollapsed ? 'space-y-3' : 'space-y-1'}>
                <NavItem to="/admet" icon={Pill} label="ADMET" />
                <NavItem
                  to="/retrosynthesis"
                  icon={FlaskConical}
                  label="Retro Synthesis"
                />
                <NavItem
                  to="/generativeai"
                  icon={BrainCircuit}
                  label="GenChem AI"
                />
                <NavItem
                  to="/virtualscreening"
                  icon={Filter}
                  label="Virtual Screening"
                />
              </div>

              <div className={isCollapsed ? 'space-y-3' : 'space-y-1'}>
                <NavItem
                  to="/docking"
                  icon={Recycle}
                  label="Molecular Docking"
                />
                  <NavItem to="/targetid" icon={Dna} label="Target DD" />
                <NavItem
                  to="/md-simulations"
                  icon={Atom}
                  // extraIcon={}
                  label="MD Simulations"
                />

                 <SectionDivider />

                 <div className={isCollapsed ? 'space-y-3 mb-6' : 'mb-4'}>
                   <SectionTitle title="Peptide Suite" />
                      <NavItem
                  to="/peptides"
                  icon={Atom}
                  // extraIcon={}
                  label="Peptides"
                />
                <NavItem
                  to="/boltz"
                  icon={FlaskRound}
                  // extraIcon={}
                  label="Boltz"
                />
                 </div>
               
              </div>
              <SectionDivider />
            </div>

            {/* Account Section */}
            <div className={isCollapsed ? 'space-y-3 mb-6' : 'mb-4'}>
              <SectionTitle title="Account" />
              <div className={isCollapsed ? 'space-y-3' : 'space-y-1'}>
                <NavItem to="/help" icon={CircleHelp} label="Help & FAQ" />
              </div>
              <SectionDivider />
            </div>

            {/* Profile Section */}
            <div className={isCollapsed ? 'space-y-3' : ''}>
              <SectionTitle title="Profile" />
              <div className={isCollapsed ? 'space-y-3' : 'space-y-1'}>
                <NavItem
                  to="/notifications"
                  icon={Bell}
                  label="Notifications"
                />
                <NavItem
                  to="/userProfileDetails"
                  icon={User}
                  label="My Profile"
                />
                
                {/* Logout button */}
                <button
                  onClick={handleLogoutRedirect}
                  className={`
                    flex items-center rounded-xl transition-all duration-200 
                    text-red-600 hover:bg-red-50 hover:shadow-sm
                    ${isCollapsed ? 'justify-center h-12 w-12 mx-auto' : 'h-12 px-3 mx-2 w-auto'}
                  `}
                >
                  <LogOut size={18} />
                  {!isCollapsed && (
                    <span className="ml-3 text-sm font-medium">Log Out</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile overlay when sidebar is open */}
      {!isCollapsed && (
        <button
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={handleCollapse}
        />
      )}

      {/* Logout confirmation modal */}
      {showLogoutModal && (
        <div className="fixed inset-0  backdrop-blur-md bg-opacity-50 flex items-center justify-center z-[999]">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <LogOut className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                Confirm Logout
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Are you sure you want to log out of your account?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SideNav;
